import './gigi-form.css'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Spinner } from '../../../Components'
import { useAuth } from '../../../Provider/AuthProvider'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { getDiffObj } from '../../Test/AutoTest/Functions'
import { getLabelDescriptionError, getStateValues, isKnownErrorCode } from '../GigiList/Functions'
import { ActionBar } from './ActionBar'
import { ConfirmModal } from './Components'
import { FormChat } from './FormChat'
import {
  getCorrectState,
  getEffectiveServerPayload,
  getErroreProcesso,
  getFormEditChanges,
  getLabelConversationCreateTransfer,
  getLabelMidofficeRef,
  getServiceCount,
  getStateDefaultData,
  getStateFromInfoServices,
  hasMeaningfulFormState,
  isGoelettForm,
  isTakeChargeAvailable
} from './Functions'
import { Icon } from './Icons'
import { ProcessingBar } from './ProcessingBar'
import { CarSection, HotelSection, PlaneSection, TrainSection } from './Sections'
import { Campi1a10Section, getCampi1a10 } from './Sections/Campi1a10'
import { SectionNav } from './Sections/Nav'
import { SearchTravellerSection } from './Sections/SearchTraveller'
import { TransferSection } from './Sections/Transfer'
import { buildTransferDefaults } from './Sections/Transfer.schema'
import { GenericTravellerSection } from './Sections/TravellerGeneric'
import { TravellerMidOfficeSection } from './Sections/TravellerMidOffice'
import { StatusBanner } from './StatusBanner'
import { TopBar } from './TopBar'
import { GigiFormValidationProvider, useGigiFormValidation } from './ValidationContext'

const language = cap.language.state || 'IT'
const t = translations[language]

const defaultConfirmType = 'createTransfer'
const { ERROR, RUNNING } = getStateValues()

/** Ogni quanto ricontrollare la conversazione mentre il processo è in esecuzione. */
const PROCESSING_POLL_MS = 5000

/**
 * Il testo del banner d'errore della conversazione.
 *
 * I codici che la lista sa descrivere (passeggeri multipli, profilo non trovato, …) sono
 * motivi di passaggio all'operatore e si leggono così. Tutti gli altri, `genericError`
 * in testa, non dicono cosa è successo e finirebbero in «Gestire da Midoffice»: qui si
 * mostra l'errore vero della unit. Il rimando al Midoffice c'è solo per gli environment che
 * ce l'hanno: su Goelett non esiste e manderebbe a cercare una pratica che non c'è.
 *
 * @param {Object} conversation - Conversazione del form
 * @param {Object} [state] - Stato corrente del form (porta `ERROR` e `errorDescriptionToOperator`)
 * @returns {string}
 */
const getTestoErrore = (conversation, state) => {
  const conMidoffice = !!conversation?.traveller?.environment?.enableMidOffice
  const coda = conMidoffice ? ` — ${t.gestireManualmente}` : ''
  if (isKnownErrorCode(conversation?.errorCode)) {
    return `${getLabelDescriptionError(conversation.errorCode)}${coda}`
  }
  const reale = getErroreProcesso(state)
  if (reale) return `${t.errore}: ${reale}${coda}`
  return `${state?.errorDescriptionToOperator || getLabelDescriptionError(conversation?.errorCode)}${coda}`
}

const formActions = {
  createTransfer: { buttonLabel: t.creazioneTrasfertaServizi, unitName: 'confirmGigiForm' },
  modifyTransfer: { buttonLabel: t.conferma, unitName: 'confirmGigiFormModify' }
}

/**
 * Restituisce il processo con id maggiore, coerente con getConversationOperatore (merge finalState).
 * @param {Array<Object>} processes - Lista processi della conversazione
 * @returns {Object|undefined} Processo più recente o undefined
 */
const selectLatestProcess = (processes = []) => {
  if (!processes?.length) return undefined
  return [...processes].sort((a, b) => b.id - a.id)[0]
}

const SECTION_META = {
  traveller: {
    id: 'traveller',
    icon: 'user',
    shortTitle: t.gfViaggiatore,
    title: t.datiViaggiatore
  },
  transfer: { id: 'transfer', icon: 'map', shortTitle: t.gfTrasferta, title: t.datiTrasferta },
  campi1a10: {
    id: 'campi1a10',
    icon: 'sliders',
    shortTitle: t.gfCampi,
    title: t.gfCampiPersonalizzati
  },
  plane: { id: 'plane', icon: 'plane', shortTitle: t.aereo, title: t.serviziAereo },
  train: { id: 'train', icon: 'train', shortTitle: t.treno, title: t.serviziTreno },
  hotel: { id: 'hotel', icon: 'hotel', shortTitle: t.hotel, title: t.serviziHotel },
  car: { id: 'car', icon: 'car', shortTitle: t.auto, title: t.serviziAuto }
}

// First user message in the conversation, used as the "Nora summary" subtitle.
const getFirstUserMessage = (conversation) => {
  const messages = conversation?.messages || []
  const first = messages
    .slice()
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .find((m) => m.sender === 'traveller')
  const text = first?.realMessageWithPPI || first?.message || ''
  return text.length > 120 ? text.slice(0, 117) + '…' : text
}

export function Form() {
  return (
    <GigiFormValidationProvider>
      <FormInner />
    </GigiFormValidationProvider>
  )
}

function FormInner() {
  const { validateAll, focusFirstError, sectionHasErrors, sectionErrorCount, totalErrorCount } =
    useGigiFormValidation()
  const { user: { user = {} } = {} } = useAuth()
  const { conversationId } = useParams()
  const { data: conversation = {}, isSuccess } = useCQuery(['conversation', conversationId])
  const stateRef = useRef()
  const formScrollRef = useRef(null)
  const [readOnly, setReadOnly] = useState(false)
  const [btnTakeCharge, setBtnTakeCharge] = useState(false)
  const [modalSendVisible, setModalSendVisible] = useState(false)
  const [modalTakeChargeVisible, setModalTakeChargeVisible] = useState(false)
  const [stateOnDB, setStateOnDB] = useState()
  /**
   * Conversazione del motore agentico: la fonte canonica è `conversation.executionEngine`.
   * Salvataggio e conferma passano da azioni diverse, perché qui non esiste un `process`.
   */
  // `conversations.executionEngine` è la fonte canonica condivisa con Executor.
  // Il campo `engine` del DTO normalizzato serve solo a descrivere il payload del form.
  const isAgentic = conversation?.executionEngine === 'agent'
  const [render, setRender] = useState(false)
  const [lastProcessStepId, setLastProcessStepId] = useState()
  const [travellerIsPresent, setTravellerIsPresent] = useState(true)
  const [isEditingTraveller, setIsEditingTraveller] = useState(false)
  const travellerSnapshotRef = useRef(null)
  /** Revisione server (processo / step) per risincronizzare il form quando cambiano i dati senza cambiare id step */
  const lastSyncedServerRevisionRef = useRef(null)
  const [lastStatus, setLastStatus] = useState()
  const [confirmType, setConfirmType] = useState()
  const [diffModify, setDiffModify] = useState({})
  const [isSubmittingGigiForm, setIsSubmittingGigiForm] = useState(false)
  const [reviewOnly, setReviewOnly] = useState(false)
  const [activeSection, setActiveSection] = useState('traveller')
  const [darkMode] = cap.darkMode.useState()
  // Sync the dark theme & defaults to <body> so the new CSS variables apply.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const body = document.body
    body.dataset.theme = darkMode ? 'dark' : 'light'
    if (!body.dataset.density) body.dataset.density = 'comfortable'
    if (!body.dataset.sticky) body.dataset.sticky = 'on'
    // AI highlight off by default — backend will eventually flag fields
    // filled by Nora and toggle this on.
    if (!body.dataset.aiHl) body.dataset.aiHl = 'off'
    return () => {
      // keep theme but reset other gigi-specific switches if we navigate away
      delete body.dataset.density
      delete body.dataset.sticky
      delete body.dataset.aiHl
    }
  }, [darkMode])

  const setState = (newState, forceRender) => {
    const mustRender = [
      ['plane', 'train', 'hotel', 'car'].some(
        (id) => getServiceCount(newState, id) !== getServiceCount(stateRef.current, id)
      ),
      getCampi1a10(newState)?.length !== getCampi1a10(stateRef.current)?.length,
      forceRender
    ]?.some((v) => v)
    stateRef.current = newState
    mustRender && setRender(!render)
  }

  useEffect(() => {
    if (!isSuccess) return
    const lastProcess = selectLatestProcess(conversation?.processes)
    const lastStatusLocal = lastProcess?.state
    setLastStatus(lastStatusLocal)
    const lastProcessStep = conversation?.lastProcessStep
    const lastPayload = getEffectiveServerPayload(conversation, lastProcessStep)
    const profiloViaggiatore =
      lastPayload?.transferInfo?.infoPax?.listOfPax[0]?.profileData?.profiloViaggiatoreWS02

    const agenticPayload = conversation?.executionEngine === 'agent'
    if (agenticPayload) {
      // Il profilo sintetico agentico non contiene `datiCliente`, ma non richiede
      // una nuova ricerca WS02: la richiesta è già intestata al traveller della
      // conversazione. Questo reset copre anche il passaggio legacy → agentico.
      setTravellerIsPresent(true)
      setIsEditingTraveller(false)
    } else if (
      (!profiloViaggiatore?.keyProfilo?.personalID ||
        !profiloViaggiatore?.datiCliente?.codiceCliente) &&
      lastStatusLocal !== RUNNING &&
      conversation?.traveller?.environment?.enableMidOffice
    ) {
      setTravellerIsPresent(false)
    }

    const { transferInfo = {}, communityPreset = {} } = lastPayload || {}
    const { infoPax = {} } = transferInfo || {}
    const { listOfPax = [] } = infoPax || {}
    const { communityPresetTraveller = {} } = listOfPax[0] || {}
    let selectedSteps
    if (Object.keys(communityPresetTraveller).length)
      selectedSteps = communityPresetTraveller?.selectedSteps
    else selectedSteps = communityPreset?.selectedSteps

    const editable =
      (conversation?.status === 'paused' ||
        (lastStatusLocal === ERROR &&
          !lastPayload?.allDataElaborated &&
          lastPayload?.gigiFormEditable) ||
        (!agenticPayload &&
          conversation?.status === 'handledByOperator' &&
          lastStatusLocal !== RUNNING)) &&
      selectedSteps !== 0 &&
      conversation?.noraIsIncluded

    const newReadOnly = !editable

    const serverRevision = [
      lastProcess?.id ?? '',
      lastProcess?.state ?? '',
      lastProcess?.updatedAt ?? '',
      lastProcessStep?.id ?? '',
      lastProcessStep?.updatedAt ?? ''
    ].join('\u0001')
    const serverDataChanged = lastSyncedServerRevisionRef.current !== serverRevision

    if (
      newReadOnly !== readOnly ||
      !stateRef.current ||
      lastProcessStepId !== lastProcessStep?.id ||
      lastProcessStep?.initialState?.forceUpdate ||
      serverDataChanged
    ) {
      lastSyncedServerRevisionRef.current = serverRevision
      setConfirmType(defaultConfirmType)
      setLastProcessStepId(lastProcessStep?.id)
      let stateDefaultData = getStateDefaultData(lastPayload)
      if (
        lastStatusLocal === RUNNING &&
        stateRef.current &&
        !hasMeaningfulFormState(lastPayload) &&
        hasMeaningfulFormState(stateRef.current)
      ) {
        stateDefaultData = stateRef.current
      }
      setStateOnDB(structuredClone(stateDefaultData))
      setReadOnly(newReadOnly)
      setState(stateDefaultData, true)
      if (defaultConfirmType) {
        const {
          transferInfo: {
            infoPax: { listOfPax: [{ infoServices = {}, oldInfoServices = {} } = {}] = [] } = {}
          } = {}
        } = stateDefaultData || {}
        if (Object.keys(oldInfoServices).length && Object.keys(infoServices).length) {
          setDiffModify(
            getDiffObj(
              getStateFromInfoServices(oldInfoServices),
              getStateFromInfoServices(infoServices)
            )
          )
        }
      }
    }
    setBtnTakeCharge(
      isTakeChargeAvailable(conversation, lastStatusLocal, user?.environment?.canTakeCharge)
    )
  }, [isSuccess, conversation])

  // Mentre Nora sta elaborando, la conversazione va ricontrollata da sé.
  //
  // La query `conversation` non ha `refetchInterval` e ha `refetchOnWindowFocus` e
  // `refetchOnReconnect` disattivati (Services/QueryCache.js): una volta caricata resta
  // ferma finché qualcosa non la invalida. Chi apriva il dettaglio mentre il processo era
  // in RUNNING si trovava quindi la barra «Elaborazione messaggio in corso» accesa per
  // sempre, anche a processo concluso da un pezzo — e con i flussi lunghi (ricerca +
  // polling fornitore) è il caso normale, non l'eccezione.
  //
  // Si poll solo finché serve: appena lo stato non è più RUNNING l'intervallo si ferma.
  useEffect(() => {
    if (lastStatus !== RUNNING) return undefined
    const timer = setInterval(() => invalidateQuery('conversation'), PROCESSING_POLL_MS)
    return () => clearInterval(timer)
  }, [lastStatus])

  const scrollSpyCleanup = useRef(null)
  const scrollLockRef = useRef(false)
  const formScrollCbRef = useCallback((node) => {
    scrollSpyCleanup.current?.()
    scrollSpyCleanup.current = null
    formScrollRef.current = node
    if (!node) return

    let debounceTimer = null
    const detectSection = () => {
      if (scrollLockRef.current) return

      const sectionEls = node.querySelectorAll('[id^="section-"]')
      if (!sectionEls.length) return

      const scrollerRect = node.getBoundingClientRect()
      const threshold = scrollerRect.top + scrollerRect.height * 0.3
      let current = null

      for (const el of sectionEls) {
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold) {
          current = el.id.replace('section-', '')
        }
      }

      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 30) {
        const lastEl = sectionEls[sectionEls.length - 1]
        if (lastEl) current = lastEl.id.replace('section-', '')
      }

      if (current) setActiveSection(current)
    }

    const handleScroll = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(detectSection, 150)
    }

    node.addEventListener('scroll', handleScroll, { passive: true })
    scrollSpyCleanup.current = () => {
      node.removeEventListener('scroll', handleScroll)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [])
  useEffect(() => () => scrollSpyCleanup.current?.(), [])

  if (!isSuccess || !stateRef.current) {
    return (
      <div className="gf-root" style={{ display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    )
  }

  const handleSend = async () => {
    setIsSubmittingGigiForm(true)
    try {
      const stateChanged = !(JSON.stringify(stateRef.current) === JSON.stringify(stateOnDB))
      const correctState = { ...getCorrectState(stateRef.current), sentFromGigiForm: true }
      const unitModifyOrCreate = formActions[confirmType]?.unitName
      // Su Goelett il form rifà la ricerca: `goelettQuotes` da solo parte senza sessione Goelett.
      const unitName =
        isGoelettForm(stateRef.current) && confirmType !== 'modifyTransfer'
          ? 'goelettRequote'
          : correctState?.processLoadQuotes || unitModifyOrCreate
      const formEditChanges = getFormEditChanges(stateOnDB, stateRef.current)

      await FC.service('info').create(
        isAgentic
          ? {
              // Non si riusa `confirmGigiForm`: quel process presuppone uno state legacy
              // che una conversazione agentica non ha. La conferma accoda un turno con la
              // stessa directive del «Procedi» del viaggiatore.
              action: 'confirmAgentRequest',
              state: correctState,
              conversationId,
              expectedRevision: stateOnDB?.agentRequestRevision ?? 0,
              formEditChanges
            }
          : {
              action: unitModifyOrCreate,
              state: correctState,
              conversationId,
              unitName,
              stateChanged,
              formEditChanges
            }
      )
      invalidateQuery(['conversation', 'conversations'])
      showToast('success', t.success, t.gfFormInviatoSuccesso)
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreConfermaForm)
    } finally {
      setIsSubmittingGigiForm(false)
    }
  }

  const updateState = async () => {
    try {
      const correctState = { ...getCorrectState(stateRef.current), sentFromGigiForm: true }
      const formEditChanges = getFormEditChanges(stateOnDB, stateRef.current)
      // Una conversazione agentica non ha un `process`: la richiesta vive su
      // `conversations.agentRequestJson` e si salva con CAS sulla revisione, perché
      // l'operatore è un writer concorrente come il viaggiatore e i turni in volo.
      await FC.service('info').create(
        isAgentic
          ? {
              action: 'updateAgentRequest',
              state: correctState,
              conversationId,
              expectedRevision: stateOnDB?.agentRequestRevision ?? 0,
              formEditChanges
            }
          : {
              action: 'updateState',
              state: correctState,
              conversationId,
              formEditChanges
            }
      )
      invalidateQuery(['conversation', 'conversations'])
      showToast('success', t.success, t.gfStateAggiornatoSuccesso)
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreAggiornamentoState)
    }
  }

  const handleTakeCharge = async () => {
    try {
      await FC.client
        .service('conversations')
        .patch(conversationId, { status: 'handledByOperator' })
      invalidateQuery(['conversation', 'conversations'])
    } catch (e) {
      showToast('error', t.errore, e?.message || 'Errore durante la presa in carico')
    }
  }

  /**
   * Apre la ricerca viaggiatore salvando uno snapshot per poter annullare
   */
  const handleStartEditTraveller = () => {
    travellerSnapshotRef.current = structuredClone(stateRef.current)
    setIsEditingTraveller(true)
  }

  /**
   * Ripristina i dati viaggiatore precedenti al click su "Modifica viaggiatore"
   */
  const handleCancelEditTraveller = () => {
    if (travellerSnapshotRef.current) {
      setState(travellerSnapshotRef.current, true)
      travellerSnapshotRef.current = null
    }
    setIsEditingTraveller(false)
  }

  const handleTravellerChosen = () => {
    travellerSnapshotRef.current = null
    setIsEditingTraveller(false)
    setTravellerIsPresent(true)
  }

  const onPrimary = async () => {
    const valid = await validateAll()
    if (!valid) {
      focusFirstError()
      return showToast('error', t.errore, t.compilareCampiObbligatori)
    }
    if (!travellerIsPresent) return showToast('error', t.errore, t.inserireDatiViaggiatore)
    setModalSendVisible(true)
  }

  const visibleSections = [
    { ...SECTION_META.traveller, visible: travellerIsPresent },
    { ...SECTION_META.transfer, visible: true },
    {
      ...SECTION_META.campi1a10,
      visible: !!getCampi1a10(stateRef.current)?.length
    },
    {
      ...SECTION_META.plane,
      visible: true,
      count: getServiceCount(stateRef.current, 'plane')
    },
    {
      ...SECTION_META.train,
      visible: true,
      count: getServiceCount(stateRef.current, 'train')
    },
    {
      ...SECTION_META.hotel,
      visible: true,
      count: getServiceCount(stateRef.current, 'hotel')
    },
    {
      ...SECTION_META.car,
      visible: true,
      count: getServiceCount(stateRef.current, 'car')
    }
  ].filter((s) => s.visible)

  const sectionsWithErrors = visibleSections.filter((s) => sectionHasErrors(s.id))
  const reviewCount = sectionsWithErrors.length
  // Use the total count of invalid fields reported by each section.
  const errorCount = totalErrorCount
  const errorSectionLabel = sectionsWithErrors[0]?.title

  const isRunning = lastStatus === RUNNING
  // Il viaggiatore è quello della chat: sull'agentico e su Goelett non si cambia dal form
  // (nessun profilo MidOffice da cercare), quindi niente «Modifica viaggiatore».
  const travellerLocked = isAgentic || isGoelettForm(stateRef.current)
  // Su Goelett non esiste una trasferta da creare: il pulsante rifà la ricerca dai dati del
  // form e manda al viaggiatore un nuovo preventivo, quindi si chiama così e chiede conferma.
  const rifaRicerca = isGoelettForm(stateRef.current) && confirmType !== 'modifyTransfer'
  const takeChargeVisible = isTakeChargeAvailable(
    conversation,
    lastStatus,
    user?.environment?.canTakeCharge
  )
  const transferDefaults = buildTransferDefaults(stateRef.current)
  const departurePlaceRequired =
    !!getServiceCount(stateRef.current, 'plane') || !!getServiceCount(stateRef.current, 'train')
  const hasMissingTransferFields =
    !transferDefaults.departureDate ||
    !transferDefaults.arrivalDate ||
    !transferDefaults.arrivalPlace?.trim() ||
    (departurePlaceRequired && !transferDefaults.departurePlace?.trim())
  const isFirstProcessing = isRunning && (conversation?.processes?.length || 0) <= 1
  const showTransferValidation =
    !readOnly ||
    (takeChargeVisible && !isRunning) ||
    (isRunning && hasMissingTransferFields && !isFirstProcessing)
  const noraQuery = getFirstUserMessage(conversation)

  // Visible vs review-only filtering
  const renderableSections = reviewOnly
    ? visibleSections.filter((s) => sectionHasErrors(s.id))
    : visibleSections

  // Estimate of fields completed by Nora (placeholder until backend reports it).
  const noraFilledCount = 0

  const scrollToSection = (id) => {
    setActiveSection(id)
    scrollLockRef.current = true
    const el = document.getElementById(`section-${id}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setTimeout(() => {
      scrollLockRef.current = false
    }, 800)
  }

  const overrideShowStatusBanner = false // false cosi ho piu spazio per il form.

  /**
   * Il numero pratica, quando lo state ce l'ha.
   *
   * Sul legacy arriva dal `transferMidOfficeRef` del processo. Su una conversazione
   * agentica quello state non esiste: i riferimenti stanno in `agentOperation`, e sulla
   * conversazione arrivano solo anno e numero trasferta. Assente, la riga non si mostra
   * e il banner resta comunque utile.
   */
  const numeroPratica =
    stateRef.current?.transferInfo?.infoPax?.listOfPax?.[0]?.transferMidOfficeRef?.numeroPratica

  return (
    <div className="gf-root">
      <div className={`gf-app${isRunning ? ' is-processing' : ''}`}>
        <TopBar stateRef={stateRef} />
        {isRunning && <ProcessingBar />}

        <div className="gf-main">
          <div className="gf-form-col">
            {overrideShowStatusBanner && !isRunning && (
              <StatusBanner
                filledFields={0}
                totalFields={0}
                noraFilledCount={noraFilledCount}
                errorCount={errorCount}
                reviewCount={reviewCount}
                reviewOnly={reviewOnly}
                onToggleReviewOnly={() => setReviewOnly((v) => !v)}
                noraQuery={noraQuery}
              />
            )}

            <SectionNav
              sections={visibleSections}
              activeId={activeSection}
              onPillClick={scrollToSection}
            />

            <div className="gf-form-scroll" ref={formScrollCbRef}>
              <div className="gf-stack">
                {conversation.errorCode && (
                  <div className="gf-banner" data-tone="danger">
                    <Icon name="alert" size={14} />
                    <span>{getTestoErrore(conversation, stateRef.current)}</span>
                  </div>
                )}

                {confirmType === 'modifyTransfer' && !isRunning && (
                  <div className="gf-banner" data-tone="info">
                    <Icon name="edit" size={14} />
                    <span>
                      {t.gfModificaTrasferta} {getLabelMidofficeRef(stateRef?.current)}
                    </span>
                  </div>
                )}

                {/*
                 * Il numero pratica non è più una precondizione del banner.
                 *
                 * Lo state del processo legacy, che una conversazione agentica non ha,
                 * nasconderebbe il banner anche con anno e numero trasferta scritti sulla
                 * conversazione dalla riconciliazione, e l'operatore non vedrebbe la
                 * pratica appena creata. Sul legacy il numero pratica c'è sempre, e la
                 * sua riga si mostra comunque.
                 */}
                {conversation?.annoTrasferta && conversation?.numeroTrasferta && (
                  <div className="gf-banner" data-tone="success">
                    <Icon name="check" size={14} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span>
                        <b>{t.identificativoAnno}:</b> {conversation?.annoTrasferta}
                      </span>
                      {numeroPratica && (
                        <span>
                          <b>{t.identificativoPratica}:</b> {numeroPratica}
                        </span>
                      )}
                      <span>
                        <b>{t.identificativoRichiesta}:</b> {conversation?.numeroTrasferta}
                      </span>
                      <span>
                        <b>{t.operatoreCreazionePratica}:</b>{' '}
                        {getLabelConversationCreateTransfer(conversation)}
                      </span>
                    </div>
                  </div>
                )}

                {(!travellerIsPresent || isEditingTraveller) && (
                  <SearchTravellerSection
                    state={[stateRef.current, setState]}
                    travellerIsPresent={[travellerIsPresent, setTravellerIsPresent]}
                    onCancel={isEditingTraveller ? handleCancelEditTraveller : undefined}
                    onTravellerChosen={handleTravellerChosen}
                    readOnly={readOnly}
                    agenticLocked={travellerLocked}
                  />
                )}

                {renderableSections.map((s) => {
                  if (s.id === 'traveller' && travellerIsPresent && !isEditingTraveller) {
                    return conversation?.traveller?.environment?.enableMidOffice ? (
                      <TravellerMidOfficeSection
                        key="traveller"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        onClickClear={handleStartEditTraveller}
                        agenticLocked={travellerLocked}
                      />
                    ) : (
                      <GenericTravellerSection
                        key="traveller"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        onClickClear={handleStartEditTraveller}
                        agenticLocked={travellerLocked}
                      />
                    )
                  }
                  if (s.id === 'transfer') {
                    return (
                      <TransferSection
                        key="transfer"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        validateOnMount={showTransferValidation}
                        enableMultiPax={conversation.traveller.environment?.enableMultiPax}
                        diff={diffModify?.transfer || []}
                      />
                    )
                  }
                  if (s.id === 'campi1a10') {
                    return (
                      <Campi1a10Section
                        key="campi1a10"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        agenticLocked={isAgentic}
                      />
                    )
                  }
                  if (s.id === 'plane') {
                    return (
                      <PlaneSection
                        key="plane"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        diff={diffModify?.plane || []}
                        enableArrivoEntro={conversation.traveller.environment?.enableArrivoEntro}
                      />
                    )
                  }
                  if (s.id === 'train') {
                    return (
                      <TrainSection
                        key="train"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        diff={diffModify?.train || []}
                        enableArrivoEntro={conversation.traveller.environment?.enableArrivoEntro}
                      />
                    )
                  }
                  if (s.id === 'hotel') {
                    return (
                      <HotelSection
                        key="hotel"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        diff={diffModify?.hotel || []}
                        enableMultiPax={conversation.traveller.environment?.enableMultiPax}
                      />
                    )
                  }
                  if (s.id === 'car') {
                    return (
                      <CarSection
                        key="car"
                        state={[stateRef.current, setState]}
                        readOnly={readOnly}
                        diff={diffModify?.car || []}
                      />
                    )
                  }
                  return null
                })}
              </div>
            </div>

            <ActionBar
              errorCount={errorCount}
              errorSectionLabel={errorSectionLabel}
              primaryLabel={
                rifaRicerca ? t.gfRifaiRicerca : formActions[confirmType]?.buttonLabel || t.conferma
              }
              onPrimary={onPrimary}
              saveDraftVisible={!readOnly && user?.role === 'admin'}
              onSaveDraft={updateState}
              takeChargeVisible={!!btnTakeCharge}
              onTakeCharge={() => setModalTakeChargeVisible(true)}
              isSubmitting={isSubmittingGigiForm}
              readOnly={readOnly}
              isRunning={isRunning}
              className={isRunning ? 'is-processing' : ''}
            />
          </div>

          <aside className="gf-chat-col" aria-label={t.gfConversazione}>
            <FormChat stateRef={stateRef} />
          </aside>
        </div>

        <ConfirmModal
          header={rifaRicerca ? t.gfRifaiRicercaTitolo : t.confermaInvio}
          label={rifaRicerca ? t.gfRifaiRicercaConferma : t.vuoiConfermareDati}
          onConfirm={handleSend}
          visible={[modalSendVisible, setModalSendVisible]}
        />
        <ConfirmModal
          header={t.prendiInCarico}
          label={t.vuoiPrendereInCarico}
          onConfirm={handleTakeCharge}
          visible={[modalTakeChargeVisible, setModalTakeChargeVisible]}
        />
      </div>
    </div>
  )
}
